// Copyright ©️ 2026 eVolve MEP, LLC

import { useActionState, useCallback, useEffect, useState } from 'react';

import type { AutodeskCloudModel } from '@evolvemep/api-ts';

import type { RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { isNotNil } from 'helpers/isNotNil';

export const useSpoolSelectionAndVisibility = (model: AutodeskCloudModel | null) => {
  const [selectedSpools, setSelectedSpools] = useState<RenderableSpool['name'][]>([]);
  const [spoolVisibility, onSpoolVisibilityChanged] = useActionState<
    // Record of Spool Name to whether it's visibility is toggled on
    Record<string, boolean>,
    {
      spoolNames: string[];
      visible: boolean;
      fresh?: boolean;
    }
  >(
    (map, { spoolNames, visible, fresh }) =>
      spoolNames.reduce(
        (out, name) => ({
          ...out,
          [name]: visible,
        }),
        fresh ? {} : { ...map },
      ),
    {},
  );

  // The "fresh" flag should only be used when we load the spools for the first time.
  // the client should only get a version of "onSpoolVisibilityChanged" that doesn't have the "fresh" flag.
  const setSpoolVisibility = useCallback(({ spoolNames, visible }: { spoolNames: string[]; visible: boolean }) => {
    onSpoolVisibilityChanged({ spoolNames, visible });
  }, []);

  // We tend to be displaying one partition of spools that's visible by default and one that's hidden by default.
  // This function helps you refresh the visibility state of those partitions.
  const refreshSpoolVisibility = useCallback(
    (visibleSpoolNames: RenderableSpool['name'][], hiddenSpoolNames: RenderableSpool['name'][]) => {
      onSpoolVisibilityChanged({ spoolNames: visibleSpoolNames, visible: true, fresh: true });
      onSpoolVisibilityChanged({ spoolNames: hiddenSpoolNames, visible: false });
    },
    [],
  );

  const onSpoolsSelected = useCallback((checked: boolean, ...spoolNames: string[]) => {
    setSelectedSpools((selected) => {
      // It's possible to receive a list of spools where some are already selected,
      // so make sure the final list is unique
      const uniqueSelected = selected.filter((n) => !spoolNames.includes(n));
      return checked ? [...uniqueSelected, ...spoolNames] : uniqueSelected;
    });
  }, []);

  const resetSelectedSpools = useCallback(() => setSelectedSpools([]), []);
  useEffect(() => {
    if (isNotNil(model)) {
      resetSelectedSpools();
    }
  }, [model, resetSelectedSpools]);

  return {
    selectedSpools,
    spoolVisibility,
    setSpoolVisibility,
    onSpoolsSelected,
    resetSelectedSpools,
    refreshSpoolVisibility,
  } as const;
};
